<template>
	<div class="official-site">
		<!-- ===== 顶部导航 ===== -->
		<header class="navbar" :class="{ scrolled: navScrolled }">
			<div class="nav-inner">
				<div class="logo">
					<div class="logo-icon">
						<svg viewBox="0 0 40 40" fill="none">
							<circle cx="20" cy="20" r="18" stroke="currentColor" stroke-width="2.5"/>
							<path d="M13 24l4.5-6 3.5 4.5L26 16l4 8" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
							<circle cx="17" cy="14" r="2.2" fill="currentColor"/>
						</svg>
					</div>
					<div class="logo-text">
						<span class="brand">旅忆</span>
						<span class="company">千帧行吟科技</span>
					</div>
				</div>
				<nav class="nav-links">
					<a href="#home" :class="{ active: activeSection === 'home' }">首页</a>
					<a href="#features" :class="{ active: activeSection === 'features' }">核心功能</a>
					<a href="#flow" :class="{ active: activeSection === 'flow' }">产品流程</a>
					<a href="#scenes" :class="{ active: activeSection === 'scenes' }">应用场景</a>
					<a href="#about" :class="{ active: activeSection === 'about' }">关于我们</a>
				</nav>
				<div class="nav-cta" @click="scrollTo('download')">
					<svg viewBox="0 0 24 24" fill="none" class="qr-mini">
						<rect x="3" y="3" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="2"/>
						<rect x="13" y="13" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="2"/>
						<path d="M13 3h4v4M3 13v4a2 2 0 002 2h4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
					</svg>
					扫码体验
				</div>
			</div>
		</header>

		<!-- ===== Hero 首屏 ===== -->
		<section class="hero" id="home">
			<div class="hero-bg">
				<div class="blob blob-1"></div>
				<div class="blob blob-2"></div>
				<div class="blob blob-3"></div>
				<div class="hero-plane">
					<svg viewBox="0 0 24 24" fill="none">
						<path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</div>
			</div>
			<div class="hero-inner">
				<div class="hero-text">
					<div class="hero-tag">
						<span class="dot"></span>
						AI 驱动的旅行记忆引擎
					</div>
					<h1>
						把每一段旅程<br/>
						<span class="gradient-text">走成诗，存成忆</span>
					</h1>
					<p class="hero-desc">
						「旅忆」是一款 AI 个性化旅行规划微信小程序。智能生成专属行程，
						地图可视化路线，轨迹实时回放，抵达即展影集，一键导出旅行视频——
						让每一次出发，都值得被认真记录。
					</p>
					<div class="hero-actions">
						<div class="btn-primary" @click="scrollTo('download')">
							<svg viewBox="0 0 24 24" fill="none"><path d="M12 2a10 10 0 100 20 10 10 0 000-20zM8.5 8.5h2v7h-2zM13.5 8.5h2v7h-2z" fill="currentColor"/></svg>
							微信小程序体验
						</div>
						<div class="btn-ghost" @click="scrollTo('features')">
							了解功能
							<svg viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
						</div>
					</div>
					<div class="hero-stats">
						<div class="stat" v-for="s in stats" :key="s.label">
							<div class="stat-num">{{ s.value }}</div>
							<div class="stat-label">{{ s.label }}</div>
						</div>
					</div>
				</div>

				<!-- 手机模拟器：AI 行程 + 地图轨迹演示 -->
				<div class="hero-phone">
					<div class="phone">
						<div class="phone-notch"></div>
						<div class="phone-screen">
							<!-- 状态栏 -->
							<div class="ps-statusbar">
								<span>9:41</span>
								<span class="ps-title">旅忆</span>
								<span class="ps-signal"></span>
							</div>
							<!-- 地图区 -->
							<div class="ps-map">
								<div class="map-grid"></div>
								<svg class="route-svg" viewBox="0 0 320 260" fill="none">
									<path class="route-line"
										d="M30 220 C 80 200, 90 160, 130 150 S 210 130, 230 90 S 280 60, 295 35"
										stroke="url(#routeGrad)" stroke-width="4" stroke-linecap="round"
										stroke-dasharray="600" stroke-dashoffset="600"/>
									<defs>
										<linearGradient id="routeGrad" x1="0" y1="1" x2="1" y2="0">
											<stop offset="0%" stop-color="#34d399"/>
											<stop offset="100%" stop-color="#22b8cf"/>
										</linearGradient>
									</defs>
								</svg>
								<div class="pin pin-start"><span>起</span></div>
								<div class="pin pin-end"><span>终</span></div>
								<div class="map-car">
									<svg viewBox="0 0 24 24" fill="none">
										<path d="M12 2L4.5 20.5l7.5-4 7.5 4L12 2z" fill="#fff" stroke="#22b8cf" stroke-width="1.6" stroke-linejoin="round"/>
									</svg>
								</div>
								<div class="map-bubble">AI 已为您规划 5 日行程</div>
							</div>
							<!-- 行程卡片 -->
							<div class="ps-card">
								<div class="card-head">
									<div class="card-title">大理 · 洱海环线</div>
									<div class="card-tag">AI 推荐</div>
								</div>
								<div class="card-days">
									<span v-for="(d, i) in 5" :key="i" :class="{ on: i < 2 }">D{{ i + 1 }}</span>
								</div>
								<div class="card-foot">
									<span>📍 12 个景点</span><span>🚗 86 km</span><span>⏱ 5 天</span>
								</div>
							</div>
							<!-- 照片墙 -->
							<div class="ps-photos">
								<div class="photo p1"></div>
								<div class="photo p2"></div>
								<div class="photo p3"></div>
							</div>
						</div>
					</div>
					<div class="float-chip chip-1">
						<svg viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
						轨迹回放中
					</div>
					<div class="float-chip chip-2">
						<svg viewBox="0 0 24 24" fill="none"><rect x="2" y="6" width="15" height="12" rx="2" stroke="currentColor" stroke-width="2"/><path d="M17 10l5-3v10l-5-3" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>
						已生成旅行视频
					</div>
				</div>
			</div>
			<div class="hero-wave">
				<svg viewBox="0 0 1440 90" preserveAspectRatio="none">
					<path d="M0,50 C240,90 480,10 720,40 C960,70 1200,20 1440,50 L1440,90 L0,90 Z" fill="#ffffff"/>
				</svg>
			</div>
		</section>

		<!-- ===== 核心功能 ===== -->
		<section class="features section" id="features">
			<div class="section-head">
				<div class="section-tag">核心功能</div>
				<h2>从规划到回忆，一站完成</h2>
				<p>四大核心能力，覆盖旅行前、中、后的完整体验</p>
			</div>
			<div class="feature-grid">
				<div class="feature-card" v-for="f in features" :key="f.title" @mouseenter="f.hover = true" @mouseleave="f.hover = false">
					<div class="f-icon" :style="{ background: f.bg, color: f.color }">
						<svg viewBox="0 0 24 24" fill="none" v-html="f.icon"></svg>
					</div>
					<h3>{{ f.title }}</h3>
					<p>{{ f.desc }}</p>
					<ul class="f-points">
						<li v-for="(p, i) in f.points" :key="i">{{ p }}</li>
					</ul>
				</div>
			</div>
		</section>

		<!-- ===== 产品流程 ===== -->
		<section class="flow section" id="flow">
			<div class="section-head light">
				<div class="section-tag">产品流程</div>
				<h2>四步，开启你的智能旅行</h2>
				<p>从一句想法，到一部可以分享的旅行影片</p>
			</div>
			<div class="flow-steps">
				<div class="flow-step" v-for="(s, i) in flowSteps" :key="i">
					<div class="step-num">0{{ i + 1 }}</div>
					<div class="step-icon" v-html="s.icon"></div>
					<h3>{{ s.title }}</h3>
					<p>{{ s.desc }}</p>
					<div class="step-arrow" v-if="i < flowSteps.length - 1">
						<svg viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
					</div>
				</div>
			</div>
		</section>

		<!-- ===== 应用场景 ===== -->
		<section class="scenes section" id="scenes">
			<div class="section-head">
				<div class="section-tag">应用场景</div>
				<h2>无论怎样的旅行，都有旅忆相伴</h2>
			</div>
			<div class="scene-list">
				<div class="scene-item" v-for="(s, i) in scenes" :key="i" :class="{ reverse: i % 2 === 1 }">
					<div class="scene-visual" :style="{ background: s.bg }">
						<div class="scene-emoji">{{ s.emoji }}</div>
						<div class="scene-chip">{{ s.chip }}</div>
					</div>
					<div class="scene-text">
						<h3>{{ s.title }}</h3>
						<p>{{ s.desc }}</p>
						<div class="scene-tags">
							<span v-for="t in s.tags" :key="t">{{ t }}</span>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- ===== 下载 / 体验 ===== -->
		<section class="download section" id="download">
			<div class="download-inner">
				<div class="dl-text">
					<h2>微信扫码，即刻启程</h2>
					<p>搜索小程序「<b>旅忆</b>」，或扫描右侧二维码<br/>让 AI 为你写下一段旅程</p>
					<div class="dl-points">
						<div class="dl-point" v-for="(p, i) in dlPoints" :key="i">
							<span class="check">✓</span>{{ p }}
						</div>
					</div>
				</div>
				<div class="dl-qrcode">
					<div class="qr-box">
						<svg viewBox="0 0 120 120" class="qr-svg">
							<rect width="120" height="120" fill="#fff"/>
							<g fill="#0f766e">
								<rect x="8" y="8" width="30" height="30" rx="3"/><rect x="14" y="14" width="18" height="18" fill="#fff"/><rect x="19" y="19" width="8" height="8"/>
								<rect x="82" y="8" width="30" height="30" rx="3"/><rect x="88" y="14" width="18" height="18" fill="#fff"/><rect x="93" y="19" width="8" height="8"/>
								<rect x="8" y="82" width="30" height="30" rx="3"/><rect x="14" y="88" width="18" height="18" fill="#fff"/><rect x="19" y="93" width="8" height="8"/>
								<rect x="48" y="8" width="8" height="8"/><rect x="64" y="16" width="8" height="8"/><rect x="48" y="32" width="16" height="8"/>
								<rect x="8" y="48" width="8" height="16"/><rect x="24" y="56" width="8" height="8"/><rect x="40" y="48" width="8" height="8"/>
								<rect x="56" y="56" width="16" height="8"/><rect x="80" y="48" width="8" height="8"/><rect x="96" y="56" width="16" height="8"/>
								<rect x="48" y="80" width="8" height="8"/><rect x="64" y="88" width="8" height="8"/><rect x="80" y="80" width="8" height="16"/>
								<rect x="48" y="104" width="16" height="8"/><rect x="88" y="104" width="24" height="8"/><rect x="96" y="80" width="8" height="8"/>
							</g>
						</svg>
						<div class="qr-logo">忆</div>
					</div>
					<div class="qr-tip">微信扫码体验小程序</div>
				</div>
			</div>
		</section>

		<!-- ===== 关于我们 ===== -->
		<section class="about section" id="about">
			<div class="about-inner">
				<div class="about-text">
					<div class="section-tag">关于我们</div>
					<h2>千帧行吟科技有限公司</h2>
					<p class="about-slogan">「千帧记录美好，行吟即是远方」</p>
					<p>
						千帧行吟科技是一家专注于智能旅行体验的科技公司。我们相信，旅行的意义不仅在抵达，
						更在于过程中的每一帧风景、每一段足迹。我们以 AI 技术为笔，以地图为卷，
						帮助每一位旅行者把走过的路由「经过」变成「作品」。
					</p>
					<div class="about-values">
						<div class="value" v-for="v in values" :key="v.title">
							<div class="v-icon">{{ v.icon }}</div>
							<div>
								<h4>{{ v.title }}</h4>
								<p>{{ v.desc }}</p>
							</div>
						</div>
					</div>
				</div>
				<div class="about-card">
					<div class="ac-row"><span>公司名称</span><b>千帧行吟科技有限公司</b></div>
					<div class="ac-row"><span>旗下产品</span><b>旅忆（微信小程序）</b></div>
					<div class="ac-row"><span>产品定位</span><b>AI 旅行规划与记忆引擎</b></div>
					<div class="ac-row"><span>核心能力</span><b>行程规划 · 地图可视化 · 轨迹回放 · 视频导出</b></div>
					<div class="ac-row"><span>联系我们</span><b>hello@qianzhenxingyin.com</b></div>
				</div>
			</div>
		</section>

		<!-- ===== 页脚 ===== -->
		<footer class="footer">
			<div class="footer-inner">
				<div class="f-brand">
					<div class="logo">
						<div class="logo-icon">
							<svg viewBox="0 0 40 40" fill="none">
								<circle cx="20" cy="20" r="18" stroke="currentColor" stroke-width="2.5"/>
								<path d="M13 24l4.5-6 3.5 4.5L26 16l4 8" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
								<circle cx="17" cy="14" r="2.2" fill="currentColor"/>
							</svg>
						</div>
						<span class="brand">旅忆</span>
					</div>
					<p>让每一次出发，都值得被认真记录。</p>
				</div>
				<div class="f-col">
					<h4>产品</h4>
					<a href="#features">核心功能</a>
					<a href="#flow">产品流程</a>
					<a href="#scenes">应用场景</a>
				</div>
				<div class="f-col">
					<h4>公司</h4>
					<a href="#about">关于我们</a>
					<a href="#download">联系我们</a>
				</div>
			</div>
			<div class="footer-bottom">
				© 2026 千帧行吟科技有限公司 · 旅忆 LvYi · 保留所有权利
			</div>
		</footer>
	</div>
</template>

<script>
	export default {
		name: 'index',
		data() {
			return {
				navScrolled: false,
				activeSection: 'home',
				stats: [
					{ value: '10万+', label: '用户选择' },
					{ value: '200+', label: '覆盖城市' },
					{ value: '98%', label: '行程满意度' },
					{ value: '50万+', label: '生成旅行视频' }
				],
				features: [
					{
						title: 'AI 个性化行程规划',
						desc: '只需告诉旅忆你的偏好、天数与预算，AI 即刻生成专属旅行方案。',
						bg: '#e0f7f4', color: '#0d9488', hover: false,
						icon: '<path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9L19 15z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>',
						points: ['兴趣标签智能匹配', '天数 / 预算灵活定制', '实时天气与路况优化']
					},
					{
						title: '地图路线可视化',
						desc: '行程自动映射到地图，每日路线、景点分布、交通方式一目了然。',
						bg: '#e3f0ff', color: '#2563eb', hover: false,
						icon: '<path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M9 4v14M15 6v14" stroke="currentColor" stroke-width="1.8"/>',
						points: ['多日路线分层展示', '景点 / 餐饮 / 住宿标注', '距离与耗时实时计算']
					},
					{
						title: '轨迹回放与影集',
						desc: '出行轨迹在地图上动态回放，抵达目的地自动弹出旅行照片。',
						bg: '#fff1e6', color: '#ea580c', hover: false,
						icon: '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/><path d="M12 7v5l3 3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
						points: ['地图轨迹动画回放', '目的地照片自动聚合', '时间轴回顾完整旅程']
					},
					{
						title: '一键导出旅行视频',
						desc: '将路线动画、沿途照片与音乐合成为短片，一键分享给好友。',
						bg: '#f3e8ff', color: '#9333ea', hover: false,
						icon: '<rect x="2" y="6" width="14" height="12" rx="2" stroke="currentColor" stroke-width="1.8"/><path d="M16 10l6-3v10l-6-3" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>',
						points: ['自动生成旅行短片', '多款清新模板可选', '朋友圈 / 好友一键分享']
					}
				],
				flowSteps: [
					{ title: '说出想法', desc: '输入目的地、天数与偏好，AI 开始为你构思行程。', icon: '<svg viewBox="0 0 24 24" fill="none"><path d="M21 12a8 8 0 01-8 8H5l-2 2V12a8 8 0 018-8h2a8 8 0 018 8z" stroke="#0d9488" stroke-width="1.8" stroke-linejoin="round"/></svg>' },
					{ title: '生成方案', desc: 'AI 输出每日行程，地图同步呈现完整路线。', icon: '<svg viewBox="0 0 24 24" fill="none"><path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2z" stroke="#0d9488" stroke-width="1.8" stroke-linejoin="round"/></svg>' },
					{ title: '出发记录', desc: '旅途中实时记录轨迹，照片自动关联地点。', icon: '<svg viewBox="0 0 24 24" fill="none"><path d="M12 21s-7-5.5-7-11a7 7 0 1114 0c0 5.5-7 11-7 11z" stroke="#0d9488" stroke-width="1.8"/><circle cx="12" cy="10" r="2.5" stroke="#0d9488" stroke-width="1.8"/></svg>' },
					{ title: '回放分享', desc: '轨迹回放 + 影集合成视频，把回忆分享出去。', icon: '<svg viewBox="0 0 24 24" fill="none"><circle cx="18" cy="5" r="3" stroke="#0d9488" stroke-width="1.8"/><circle cx="6" cy="12" r="3" stroke="#0d9488" stroke-width="1.8"/><circle cx="18" cy="19" r="3" stroke="#0d9488" stroke-width="1.8"/><path d="M8.6 10.5l6.8-4M8.6 13.5l6.8 4" stroke="#0d9488" stroke-width="1.8"/></svg>' }
				],
				scenes: [
					{
						emoji: '🏞', chip: '周末出逃计划',
						title: '周末周边微旅行',
						desc: '只有两天也能玩出花样。AI 根据你的出发地推荐周边小众路线，不赶路、不踩雷，让周末真正属于自己。',
						tags: ['周边游', '小众路线', '2天1夜'],
						bg: 'linear-gradient(135deg,#d3f5ee 0%,#a8e6cf 100%)'
					},
					{
						emoji: '👨‍👩‍👧', chip: '亲子安心之选',
						title: '家庭亲子出行',
						desc: '自动避开拥挤时段与高强度路线，规划亲子友好的节奏与设施，全家出行轻松又安心。',
						tags: ['亲子友好', '轻松节奏', '设施完善'],
						bg: 'linear-gradient(135deg,#fde8d8 0%,#ffd3a5 100%)'
					},
					{
						emoji: '🎒', chip: '一个人也精彩',
						title: '独自深度旅行',
						desc: '为独行者量身打造深度路线，串联城市人文与自然秘境，轨迹回放让每段独行都有迹可循。',
						tags: ['深度游', '人文探索', '轨迹记录'],
						bg: 'linear-gradient(135deg,#e2e8ff 0%,#c3d0ff 100%)'
					},
					{
						emoji: '💑', chip: '记录我们的路',
						title: '情侣纪念之旅',
						desc: '把两个人的旅程做成一部专属影片：路线动画串联照片与回忆，是旅途的纪念，也是最好的礼物。',
						tags: ['纪念日', '旅行影片', '回忆收藏'],
						bg: 'linear-gradient(135deg,#ffe3ec 0%,#ffc9d9 100%)'
					}
				],
				dlPoints: ['免费使用，无需下载安装', 'AI 规划 30 秒出方案', '旅行视频无限次生成'],
				values: [
					{ icon: '🧭', title: '以用户为中心', desc: '从真实旅行需求出发，打磨每一处体验细节。' },
					{ icon: '🤖', title: '技术驱动美好', desc: '用 AI 与地图技术，让规划与记录变得更简单。' },
					{ icon: '🌱', title: '记录即珍藏', desc: '相信每一段旅程，都值得被温柔地保存。' }
				]
			}
		},
		mounted() {
			window.addEventListener('scroll', this.onScroll)
		},
		beforeDestroy() {
			window.removeEventListener('scroll', this.onScroll)
		},
		methods: {
			onScroll() {
				this.navScrolled = window.scrollY > 40
				const ids = ['home', 'features', 'flow', 'scenes', 'about']
				for (let i = ids.length - 1; i >= 0; i--) {
					const el = document.getElementById(ids[i])
					if (el && el.getBoundingClientRect().top <= 120) {
						this.activeSection = ids[i]
						break
					}
				}
			},
			scrollTo(id) {
				const el = document.getElementById(id)
				if (el) el.scrollIntoView({ behavior: 'smooth' })
			}
		}
	}
</script>

<style scoped lang="scss">
	$primary: #0d9488;
	$primary-light: #14b8a6;
	$cyan: #22b8cf;
	$ink: #1f2937;
	$sub: #6b7280;
	$bg-mint: #f2faf8;

	* { box-sizing: border-box; margin: 0; padding: 0; }

	.official-site {
		font-family: "PingFang SC", "Microsoft YaHei", "Helvetica Neue", sans-serif;
		color: $ink;
		background: #fff;
		overflow-x: hidden;
	}

	a { text-decoration: none; color: inherit; cursor: pointer; }

	/* ================= 导航 ================= */
	.navbar {
		position: fixed; top: 0; left: 0; right: 0; z-index: 100;
		transition: all .3s;
		&.scrolled {
			background: rgba(255, 255, 255, .92);
			backdrop-filter: blur(12px);
			box-shadow: 0 2px 20px rgba(13, 148, 136, .08);
			.nav-links a, .logo-text .company { color: $ink; }
			.logo-text .brand { color: $primary; }
			.logo-icon { color: $primary; }
			.nav-cta { background: $primary; color: #fff; border-color: $primary; }
		}
	}
	.nav-inner {
		max-width: 1200px; margin: 0 auto; padding: 0 24px;
		height: 68px; display: flex; align-items: center; gap: 40px;
	}
	.logo {
		display: flex; align-items: center; gap: 10px;
		.logo-icon { width: 36px; height: 36px; color: $primary; svg { width: 100%; height: 100%; } }
		.logo-text {
			display: flex; flex-direction: column; line-height: 1.2;
			.brand { font-size: 20px; font-weight: 700; color: $primary; letter-spacing: 2px; }
			.company { font-size: 11px; color: $sub; letter-spacing: 1px; }
		}
	}
	.nav-links {
		flex: 1; display: flex; gap: 32px; justify-content: center;
		a {
			font-size: 15px; color: $ink; position: relative; padding: 4px 0;
			&.active { color: $primary; font-weight: 600; }
			&.active::after {
				content: ''; position: absolute; left: 50%; bottom: -4px;
				transform: translateX(-50%); width: 16px; height: 3px;
				border-radius: 2px; background: $primary;
			}
			&:hover { color: $primary; }
		}
	}
	.nav-cta {
		display: flex; align-items: center; gap: 6px;
		padding: 8px 18px; border-radius: 20px; font-size: 14px; font-weight: 600;
		border: 1.5px solid $primary; color: $primary; cursor: pointer; transition: all .25s;
		.qr-mini { width: 16px; height: 16px; }
		&:hover { background: $primary; color: #fff; }
	}

	/* ================= Hero ================= */
	.hero {
		position: relative; min-height: 100vh;
		background: linear-gradient(160deg, #e6faf5 0%, #f0fbf4 45%, #e8f6ff 100%);
		display: flex; flex-direction: column; overflow: hidden;
	}
	.hero-bg { position: absolute; inset: 0; pointer-events: none; }
	.blob {
		position: absolute; border-radius: 50%; filter: blur(60px); opacity: .5;
	}
	.blob-1 { width: 420px; height: 420px; background: #99f0dc; top: -100px; left: -80px; animation: drift 12s ease-in-out infinite alternate; }
	.blob-2 { width: 360px; height: 360px; background: #b8e3ff; bottom: 60px; right: -60px; animation: drift 14s ease-in-out infinite alternate-reverse; }
	.blob-3 { width: 240px; height: 240px; background: #d8f9c4; top: 40%; left: 42%; animation: drift 10s ease-in-out infinite alternate; }
	@keyframes drift {
		from { transform: translate(0, 0) scale(1); }
		to { transform: translate(40px, 30px) scale(1.1); }
	}
	.hero-plane {
		position: absolute; top: 16%; right: 14%; width: 54px; height: 54px; color: $primary;
		opacity: .35; animation: fly 6s ease-in-out infinite;
		svg { width: 100%; height: 100%; }
	}
	@keyframes fly {
		0%, 100% { transform: translate(0, 0) rotate(-8deg); }
		50% { transform: translate(-40px, 26px) rotate(4deg); }
	}
	.hero-inner {
		position: relative; z-index: 2;
		max-width: 1200px; margin: 0 auto; padding: 140px 24px 120px;
		display: flex; align-items: center; gap: 60px; flex: 1;
		width: 100%;
	}
	.hero-text { flex: 1.1; min-width: 0; }
	.hero-tag {
		display: inline-flex; align-items: center; gap: 8px;
		padding: 6px 16px; border-radius: 20px; font-size: 13px; font-weight: 600;
		color: $primary; background: rgba(13, 148, 136, .1);
		border: 1px solid rgba(13, 148, 136, .25); margin-bottom: 24px;
		.dot { width: 8px; height: 8px; border-radius: 50%; background: $primary-light; animation: pulse 1.6s infinite; }
	}
	@keyframes pulse {
		0%, 100% { box-shadow: 0 0 0 0 rgba(20, 184, 166, .5); }
		50% { box-shadow: 0 0 0 6px rgba(20, 184, 166, 0); }
	}
	.hero-text h1 {
		font-size: 52px; line-height: 1.25; font-weight: 800; letter-spacing: 1px;
		margin-bottom: 22px;
	}
	.gradient-text {
		background: linear-gradient(92deg, $primary 10%, $cyan 60%, #4ade80 100%);
		-webkit-background-clip: text; background-clip: text; color: transparent;
	}
	.hero-desc {
		font-size: 16px; line-height: 1.9; color: $sub; max-width: 520px; margin-bottom: 36px;
	}
	.hero-actions { display: flex; gap: 16px; margin-bottom: 48px; flex-wrap: wrap; }
	.btn-primary {
		display: inline-flex; align-items: center; gap: 10px;
		padding: 15px 30px; border-radius: 30px; font-size: 16px; font-weight: 700;
		color: #fff; cursor: pointer;
		background: linear-gradient(92deg, $primary, $cyan);
		box-shadow: 0 8px 24px rgba(13, 148, 136, .35);
		transition: transform .25s, box-shadow .25s;
		svg { width: 20px; height: 20px; }
		&:hover { transform: translateY(-3px); box-shadow: 0 12px 32px rgba(13, 148, 136, .45); }
	}
	.btn-ghost {
		display: inline-flex; align-items: center; gap: 8px;
		padding: 15px 26px; border-radius: 30px; font-size: 16px; font-weight: 600;
		color: $primary; cursor: pointer; border: 1.5px solid rgba(13, 148, 136, .4);
		background: rgba(255, 255, 255, .6); transition: all .25s;
		svg { width: 18px; height: 18px; transition: transform .25s; }
		&:hover { background: #fff; border-color: $primary; svg { transform: translateX(4px); } }
	}
	.hero-stats { display: flex; gap: 40px; flex-wrap: wrap; }
	.stat-num {
		font-size: 30px; font-weight: 800;
		background: linear-gradient(92deg, $primary, $cyan);
		-webkit-background-clip: text; background-clip: text; color: transparent;
	}
	.stat-label { font-size: 13px; color: $sub; margin-top: 2px; }

	/* 手机模拟器 */
	.hero-phone { flex: 1; display: flex; justify-content: center; position: relative; min-width: 320px; }
	.phone {
		width: 320px; border-radius: 42px; padding: 12px;
		background: #fff; box-shadow: 0 30px 80px rgba(13, 148, 136, .22), 0 4px 16px rgba(0, 0, 0, .06);
		position: relative;
	}
	.phone-notch {
		position: absolute; top: 12px; left: 50%; transform: translateX(-50%);
		width: 110px; height: 24px; background: #fff; border-radius: 0 0 16px 16px; z-index: 3;
	}
	.phone-screen {
		border-radius: 32px; overflow: hidden; background: $bg-mint;
		height: 600px; display: flex; flex-direction: column; position: relative;
	}
	.ps-statusbar {
		display: flex; justify-content: space-between; align-items: center;
		padding: 10px 20px 8px; font-size: 12px; font-weight: 600; color: $ink;
		.ps-title { color: $primary; letter-spacing: 3px; font-weight: 700; }
		.ps-signal { width: 34px; height: 12px; border-radius: 4px; background: linear-gradient(90deg, $primary 60%, #d1d5db 60%); }
	}
	.ps-map {
		flex: 1; margin: 6px 12px 0; border-radius: 18px; position: relative; overflow: hidden;
		background: linear-gradient(150deg, #dcf5ec, #e8f3ff);
	}
	.map-grid {
		position: absolute; inset: 0;
		background-image: linear-gradient(rgba(13, 148, 136, .07) 1px, transparent 1px),
			linear-gradient(90deg, rgba(13, 148, 136, .07) 1px, transparent 1px);
		background-size: 34px 34px;
	}
	.route-svg { position: absolute; inset: 0; width: 100%; height: 100%; }
	.route-line { animation: draw 3.5s ease forwards .5s; }
	@keyframes draw { to { stroke-dashoffset: 0; } }
	.pin {
		position: absolute; width: 26px; height: 26px; border-radius: 50% 50% 50% 0;
		transform: rotate(-45deg); display: flex; align-items: center; justify-content: center;
		span { transform: rotate(45deg); font-size: 11px; font-weight: 700; color: #fff; }
	}
	.pin-start { left: 20px; bottom: 28px; background: #4ade80; box-shadow: 0 4px 10px rgba(74, 222, 128, .5); }
	.pin-end { right: 14px; top: 18px; background: #fb7185; box-shadow: 0 4px 10px rgba(251, 113, 133, .5); }
	.map-car {
		position: absolute; width: 30px; height: 30px; z-index: 2;
		offset-path: path("M30 220 C 80 200, 90 160, 130 150 S 210 130, 230 90 S 280 60, 295 35");
		animation: drive 6s ease-in-out infinite;
		svg { width: 100%; height: 100%; filter: drop-shadow(0 3px 6px rgba(34, 184, 207, .5)); }
	}
	@keyframes drive {
		0% { offset-distance: 0%; }
		50% { offset-distance: 100%; }
		100% { offset-distance: 0%; }
	}
	.map-bubble {
		position: absolute; top: 12px; left: 50%; transform: translateX(-50%);
		background: #fff; padding: 6px 14px; border-radius: 14px;
		font-size: 12px; font-weight: 600; color: $primary; white-space: nowrap;
		box-shadow: 0 4px 12px rgba(0, 0, 0, .1);
	}
	.ps-card {
		margin: 12px; background: #fff; border-radius: 16px; padding: 14px 16px;
		box-shadow: 0 4px 14px rgba(13, 148, 136, .08);
	}
	.card-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
	.card-title { font-size: 15px; font-weight: 700; }
	.card-tag {
		font-size: 11px; font-weight: 700; color: #fff; padding: 3px 10px; border-radius: 10px;
		background: linear-gradient(92deg, $primary, $cyan);
	}
	.card-days { display: flex; gap: 8px; margin-bottom: 10px; }
	.card-days span {
		flex: 1; text-align: center; padding: 5px 0; border-radius: 8px;
		font-size: 12px; font-weight: 600; color: $sub; background: #f3f4f6;
		&.on { color: #fff; background: $primary; }
	}
	.card-foot { display: flex; justify-content: space-between; font-size: 12px; color: $sub; }
	.ps-photos { display: flex; gap: 10px; padding: 0 12px 14px; }
	.photo {
		flex: 1; height: 64px; border-radius: 12px;
		background-size: cover; box-shadow: inset 0 -20px 30px rgba(0, 0, 0, .12);
	}
	.p1 { background: linear-gradient(160deg, #7dd3fc 0%, #38bdf8 45%, #34d399 100%); }
	.p2 { background: linear-gradient(160deg, #fcd34d 0%, #fb923c 55%, #f472b6 100%); }
	.p3 { background: linear-gradient(160deg, #86efac 0%, #4ade80 50%, #14b8a6 100%); }
	.float-chip {
		position: absolute; display: flex; align-items: center; gap: 8px;
		padding: 10px 16px; border-radius: 16px; font-size: 13px; font-weight: 700;
		background: rgba(255, 255, 255, .95); color: $primary;
		box-shadow: 0 10px 30px rgba(13, 148, 136, .2);
		svg { width: 18px; height: 18px; }
	}
	.chip-1 { left: -30px; top: 22%; animation: bob 4s ease-in-out infinite; }
	.chip-2 { right: -24px; bottom: 18%; animation: bob 4s ease-in-out infinite 1.2s; }
	@keyframes bob {
		0%, 100% { transform: translateY(0); }
		50% { transform: translateY(-12px); }
	}
	.hero-wave { position: relative; z-index: 2; margin-bottom: -2px; svg { display: block; width: 100%; height: 90px; } }

	/* ================= 通用 Section ================= */
	.section { padding: 96px 24px; }
	.section-head { max-width: 760px; margin: 0 auto 60px; text-align: center; }
	.section-tag {
		display: inline-block; padding: 5px 16px; border-radius: 16px;
		font-size: 13px; font-weight: 700; letter-spacing: 2px;
		color: $primary; background: rgba(13, 148, 136, .1); margin-bottom: 16px;
	}
	.section-head h2 { font-size: 36px; font-weight: 800; margin-bottom: 14px; }
	.section-head p { font-size: 16px; color: $sub; }
	.section-head.light { h2 { color: #fff; } p { color: rgba(255, 255, 255, .75); } .section-tag { background: rgba(255, 255, 255, .15); color: #a7f3ea; } }

	/* ================= 核心功能 ================= */
	.features { background: #fff; }
	.feature-grid {
		max-width: 1200px; margin: 0 auto;
		display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px;
	}
	.feature-card {
		padding: 32px 26px; border-radius: 22px; background: $bg-mint;
		border: 1.5px solid transparent; transition: all .3s; cursor: default;
		&:hover {
			background: #fff; border-color: rgba(13, 148, 136, .3);
			transform: translateY(-8px); box-shadow: 0 20px 40px rgba(13, 148, 136, .14);
		}
		h3 { font-size: 19px; font-weight: 700; margin: 18px 0 10px; }
		p { font-size: 14px; line-height: 1.8; color: $sub; margin-bottom: 14px; }
	}
	.f-icon {
		width: 56px; height: 56px; border-radius: 16px;
		display: flex; align-items: center; justify-content: center;
		svg { width: 28px; height: 28px; }
	}
	.f-points { list-style: none; }
	.f-points li {
		font-size: 13px; color: $sub; padding: 5px 0 5px 20px; position: relative;
		&::before {
			content: ''; position: absolute; left: 0; top: 11px;
			width: 10px; height: 10px; border-radius: 50%;
			background: linear-gradient(135deg, $primary-light, $cyan);
		}
	}

	/* ================= 产品流程 ================= */
	.flow {
		background: linear-gradient(150deg, #0f766e 0%, #0d9488 55%, #0891b2 100%);
		position: relative; overflow: hidden;
		&::before {
			content: ''; position: absolute; width: 500px; height: 500px; border-radius: 50%;
			background: rgba(255, 255, 255, .06); top: -200px; right: -120px;
		}
		&::after {
			content: ''; position: absolute; width: 360px; height: 360px; border-radius: 50%;
			background: rgba(255, 255, 255, .05); bottom: -140px; left: -100px;
		}
	}
	.flow-steps {
		max-width: 1200px; margin: 0 auto; position: relative; z-index: 2;
		display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px;
	}
	.flow-step {
		position: relative; background: rgba(255, 255, 255, .1);
		border: 1px solid rgba(255, 255, 255, .18); backdrop-filter: blur(6px);
		border-radius: 20px; padding: 30px 24px; transition: all .3s;
		&:hover { background: rgba(255, 255, 255, .18); transform: translateY(-6px); }
		h3 { color: #fff; font-size: 18px; margin: 16px 0 10px; }
		p { color: rgba(255, 255, 255, .8); font-size: 13.5px; line-height: 1.8; }
	}
	.step-num {
		font-size: 40px; font-weight: 800; color: rgba(255, 255, 255, .25); line-height: 1;
	}
	.step-icon { width: 44px; height: 44px; margin-top: 10px; svg { width: 100%; height: 100%; } }
	.step-arrow {
		position: absolute; right: -18px; top: 50%; transform: translateY(-50%);
		width: 28px; height: 28px; color: rgba(255, 255, 255, .6); z-index: 3;
		svg { width: 100%; height: 100%; }
	}

	/* ================= 应用场景 ================= */
	.scenes { background: #fff; }
	.scene-list { max-width: 1000px; margin: 0 auto; display: flex; flex-direction: column; gap: 64px; }
	.scene-item {
		display: flex; align-items: center; gap: 56px;
		&.reverse { flex-direction: row-reverse; }
	}
	.scene-visual {
		flex: 1; height: 260px; border-radius: 24px; position: relative;
		display: flex; align-items: center; justify-content: center;
		box-shadow: 0 16px 40px rgba(0, 0, 0, .08); transition: transform .3s;
		&:hover { transform: scale(1.03) rotate(-1deg); }
		.scene-emoji { font-size: 88px; filter: drop-shadow(0 8px 16px rgba(0, 0, 0, .15)); }
		.scene-chip {
			position: absolute; bottom: 18px; left: 18px;
			padding: 7px 16px; border-radius: 14px; font-size: 13px; font-weight: 700;
			background: rgba(255, 255, 255, .9); color: $primary;
			box-shadow: 0 4px 12px rgba(0, 0, 0, .08);
		}
	}
	.scene-text {
		flex: 1.2;
		h3 { font-size: 26px; font-weight: 800; margin-bottom: 14px; }
		p { font-size: 15px; line-height: 1.9; color: $sub; margin-bottom: 18px; }
	}
	.scene-tags { display: flex; gap: 10px; flex-wrap: wrap; }
	.scene-tags span {
		padding: 6px 16px; border-radius: 16px; font-size: 13px; font-weight: 600;
		color: $primary; background: rgba(13, 148, 136, .09);
		border: 1px solid rgba(13, 148, 136, .2);
	}

	/* ================= 下载 ================= */
	.download { background: $bg-mint; }
	.download-inner {
		max-width: 1000px; margin: 0 auto; display: flex; align-items: center; gap: 80px;
		background: #fff; border-radius: 28px; padding: 60px 70px;
		box-shadow: 0 20px 60px rgba(13, 148, 136, .12);
	}
	.dl-text {
		flex: 1;
		h2 { font-size: 32px; font-weight: 800; margin-bottom: 14px; }
		p { font-size: 15px; line-height: 1.9; color: $sub; margin-bottom: 24px; b { color: $primary; } }
	}
	.dl-points { display: flex; flex-direction: column; gap: 10px; }
	.dl-point {
		display: flex; align-items: center; gap: 10px; font-size: 14px; color: $ink;
		.check {
			width: 20px; height: 20px; border-radius: 50%; font-size: 12px; font-weight: 800;
			display: inline-flex; align-items: center; justify-content: center;
			color: #fff; background: linear-gradient(135deg, $primary-light, $cyan);
		}
	}
	.dl-qrcode { text-align: center; }
	.qr-box {
		position: relative; width: 190px; height: 190px; border-radius: 20px; overflow: hidden;
		border: 6px solid #fff; box-shadow: 0 12px 36px rgba(13, 148, 136, .2);
		.qr-svg { width: 100%; height: 100%; display: block; }
		.qr-logo {
			position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
			width: 42px; height: 42px; border-radius: 12px;
			display: flex; align-items: center; justify-content: center;
			font-size: 22px; font-weight: 800; color: #fff;
			background: linear-gradient(135deg, $primary, $cyan);
			box-shadow: 0 4px 12px rgba(13, 148, 136, .4);
		}
	}
	.qr-tip { margin-top: 14px; font-size: 13px; color: $sub; font-weight: 600; }

	/* ================= 关于我们 ================= */
	.about { background: #fff; }
	.about-inner { max-width: 1100px; margin: 0 auto; display: flex; gap: 70px; align-items: flex-start; }
	.about-text {
		flex: 1.3;
		h2 { font-size: 32px; font-weight: 800; margin-bottom: 10px; }
		.about-slogan { font-size: 16px; color: $primary; font-weight: 700; margin-bottom: 18px; letter-spacing: 1px; }
		p { font-size: 15px; line-height: 2; color: $sub; margin-bottom: 28px; }
		.section-tag { margin-bottom: 14px; }
	}
	.about-values { display: flex; flex-direction: column; gap: 18px; }
	.value {
		display: flex; gap: 14px; align-items: flex-start;
		.v-icon {
			width: 44px; height: 44px; border-radius: 12px; font-size: 20px; flex-shrink: 0;
			display: flex; align-items: center; justify-content: center; background: $bg-mint;
		}
		h4 { font-size: 15px; font-weight: 700; margin-bottom: 4px; }
		p { font-size: 13.5px; line-height: 1.7; margin: 0; }
	}
	.about-card {
		flex: 1; background: linear-gradient(160deg, $bg-mint, #eaf6ff);
		border-radius: 22px; padding: 34px 30px; border: 1px solid rgba(13, 148, 136, .12);
	}
	.ac-row {
		padding: 14px 0; border-bottom: 1px dashed rgba(13, 148, 136, .2);
		&:last-child { border-bottom: none; }
		span { display: block; font-size: 12px; color: $sub; margin-bottom: 4px; letter-spacing: 1px; }
		b { font-size: 15px; color: $ink; }
	}

	/* ================= 页脚 ================= */
	.footer { background: #0b3f3a; color: rgba(255, 255, 255, .8); }
	.footer-inner {
		max-width: 1100px; margin: 0 auto; padding: 60px 24px 40px;
		display: flex; gap: 80px; flex-wrap: wrap;
	}
	.f-brand {
		flex: 1.5; min-width: 240px;
		.logo { color: #a7f3ea; margin-bottom: 14px; .brand { font-size: 22px; font-weight: 800; color: #fff; letter-spacing: 3px; } .logo-icon { width: 34px; height: 34px; } }
		p { font-size: 14px; line-height: 1.8; }
	}
	.f-col {
		flex: 1; min-width: 120px;
		h4 { color: #fff; font-size: 15px; margin-bottom: 16px; }
		a { display: block; font-size: 13.5px; padding: 5px 0; transition: color .2s; &:hover { color: #5eead4; } }
	}
	.footer-bottom {
		border-top: 1px solid rgba(255, 255, 255, .1);
		text-align: center; padding: 20px; font-size: 13px; color: rgba(255, 255, 255, .5);
	}

	/* ================= 响应式 ================= */
	@media (max-width: 1080px) {
		.feature-grid, .flow-steps { grid-template-columns: repeat(2, 1fr); }
		.step-arrow { display: none; }
		.hero-inner { flex-direction: column; padding-top: 120px; text-align: center; gap: 60px; }
		.hero-desc { margin: 0 auto 36px; }
		.hero-actions, .hero-stats { justify-content: center; }
		.hero-text h1 { font-size: 40px; }
		.about-inner, .download-inner { flex-direction: column; gap: 40px; padding: 40px; }
	}
	@media (max-width: 640px) {
		.nav-links { display: none; }
		.feature-grid, .flow-steps { grid-template-columns: 1fr; }
		.scene-item, .scene-item.reverse { flex-direction: column; gap: 24px; }
		.hero-text h1 { font-size: 32px; }
		.section-head h2 { font-size: 26px; }
		.float-chip { display: none; }
		.section { padding: 64px 18px; }
	}
</style>
